import { DEFAULT_EVENT_TIMEZONE } from '@/lib/eventTime'

/**
 * Event-local wall clock → instant, exactly as the backend's
 * `vendor/pricing.py` `localize` does it, whatever the browser's timezone.
 *
 * DST, the two cases:
 * - Fall back: a wall-clock time that happens twice maps to the EARLIEST
 *   instant, its first occurrence (Python `fold=0`). America/Toronto
 *   2026-11-01 01:30 → 05:30Z (EDT), not 06:30Z (EST).
 * - Spring forward: a wall-clock time that never happens steps forward a
 *   minute at a time to the first one that does. America/Toronto
 *   2026-03-08 02:30 → 03:00 EDT = 07:00Z.
 *
 * date-fns-tz's `fromZonedTime` does neither reliably: in a gap it goes
 * back an hour, and which occurrence of a repeated hour it picks depends on
 * the browser's zone. `@/lib/eventTime` keeps using it for other features.
 */

/** Days of the week as the API numbers them: 0 = Monday … 6 = Sunday (Python's `weekday()`). */
export const DAYS: { value: number; short: string; long: string }[] = [
  { value: 0, short: 'Mon', long: 'Monday' },
  { value: 1, short: 'Tue', long: 'Tuesday' },
  { value: 2, short: 'Wed', long: 'Wednesday' },
  { value: 3, short: 'Thu', long: 'Thursday' },
  { value: 4, short: 'Fri', long: 'Friday' },
  { value: 5, short: 'Sat', long: 'Saturday' },
  { value: 6, short: 'Sun', long: 'Sunday' },
]

const zoneFormats = new Map<string, Intl.DateTimeFormat>()

function zoneFormat(timeZone: string): Intl.DateTimeFormat {
  let fmt = zoneFormats.get(timeZone)
  if (!fmt) {
    try {
      fmt = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    } catch {
      // An unknown zone reads as the default, like the backend's `event_zone`.
      if (timeZone === DEFAULT_EVENT_TIMEZONE) throw new RangeError(`Unknown timezone ${timeZone}`)
      fmt = zoneFormat(DEFAULT_EVENT_TIMEZONE)
    }
    zoneFormats.set(timeZone, fmt)
  }
  return fmt
}

/** The zone's wall clock at instant `ms`, written as if that wall clock were UTC. */
function wallClockAt(ms: number, timeZone: string): number {
  const fields: Record<string, number> = {}
  for (const part of zoneFormat(timeZone).formatToParts(new Date(ms))) fields[part.type] = Number(part.value)
  return Date.UTC(fields.year, fields.month - 1, fields.day, fields.hour % 24, fields.minute, fields.second)
}

const MINUTE = 60_000
const DAY = 86_400_000
// `localize` gives up after 180 one-minute steps.
const MAX_GAP_STEPS = 180

// Status is worked out for every row each clock tick, mostly for the same
// few days and times, and each answer costs a few Intl calls.
const instants = new Map<string, number>()
const MAX_CACHED = 5000

/**
 * An event-local date ("YYYY-MM-DD") and wall clock ("HH:MM" or
 * "HH:MM:SS") as an instant. Invalid Date when either is unreadable.
 */
export function wallClockToInstant(dateYmd: string, timeHm: string, timeZone: string): Date {
  const key = `${dateYmd}T${timeHm}|${timeZone}`
  let instant = instants.get(key)
  if (instant === undefined) {
    instant = resolve(dateYmd, timeHm, timeZone)
    if (instants.size >= MAX_CACHED) instants.clear()
    instants.set(key, instant)
  }
  return new Date(instant)
}

function resolve(dateYmd: string, timeHm: string, timeZone: string): number {
  const date = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateYmd)
  const clock = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(timeHm)
  if (!date || !clock) return NaN
  const naive = Date.UTC(+date[1], +date[2] - 1, +date[3], +clock[1], +clock[2], +(clock[3] ?? 0))
  if (Number.isNaN(naive)) return NaN
  // The zone's offsets a day either side bracket any transition near the
  // wall-clock time (offsets are under a day), so they are the only
  // candidates; the first is the offset before the transition. The same
  // offset on both sides means no transition: one answer.
  const before = wallClockAt(naive - DAY, timeZone) - (naive - DAY)
  const after = wallClockAt(naive + DAY, timeZone) - (naive + DAY)
  if (before === after) return naive - before
  for (let step = 0; step <= MAX_GAP_STEPS; step++) {
    const wall = naive + step * MINUTE
    const matches = [wall - before, wall - after].filter((instant) => wallClockAt(instant, timeZone) === wall)
    if (matches.length) return Math.min(...matches)
  }
  // No valid time within three hours (not a real zone): the pre-transition offset.
  return naive - before
}
